import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';

import { SharedService } from '@services/shared.service';
import { ApiAccountService } from '@services/api-account.service';
import { FormBase } from '@shared/form-base.component';

import { CustomerModel } from '@models/CustomerModel';
import { RegisterModel, RegisterPhoneModel } from '@models/RegisterModel';
import { SweetAlertResult } from 'sweetalert2';
import { Router } from '@angular/router';
import { SessionManager } from '@services/session-manager';


@Component({
	selector: 'app-verify-account',
	templateUrl: './verify-account.component.html',
	styleUrls: ['./verify-account.component.scss']
})
export class VerifyAccountComponent extends FormBase<RegisterModel> implements OnInit {

	@Input() user: CustomerModel;
	@Output() validVerificationPhone: EventEmitter<boolean> = new EventEmitter<boolean>();

	// Evento para ejecutar en el componente hijo
	onReset = new Subject<boolean>()

	formGroup: UntypedFormGroup;
	showModifyPhone: boolean = true;
	requestAt = new Date();
	codeAlert: string;

	constructor(public sharedService: SharedService, private formBuilder: UntypedFormBuilder, 
		private apiAccountService: ApiAccountService, private router: Router) {
		super();
	}

	ngOnInit(): void {
		this._formInit();		
	}

	// Form

	isInvalidCode(): boolean {

        return this.codeConfirmation?.value?.length != 6;
    }

	get phone() { return this._formGet(this.formGroup, 'phone'); }
	get invalidPhone(): boolean {
		if (this._isInvalid(this.formGroup, 'phone', this.touched)) {
			return true;
		}
		return false;
	}

	get codeConfirmation() { return this._formGet(this.formGroup, 'codeConfirmation'); }
    get invalidCodeConfirmation(): boolean {
        return this._isInvalid(this.formGroup, 'codeConfirmation', this.touched);
    }


	private _formInit(): void {

		// Reset for hide form errors
		this.touched = true;

		this.formGroup = this.formBuilder.group(
			{
				phoneCountry: new UntypedFormControl(
					this.user.phoneCountry,
					[Validators.required]
				),
				phone: [
					{ number: this.user?.phone, countryCode: 'CR', dialCode: '+506' },
					[Validators.required]
				],
				codeConfirmation: new UntypedFormControl(
					'',
					[Validators.required]
				)
			}
		);
	}

	// End Form

	onVerifyPhone(showAlert = false) {

		if (this.invalidPhone) return;

		this.requestAt = new Date();

		const registerModel = new RegisterModel({
			phoneCountry: this.phone.value?.dialCode,
			phone: this.phone.value?.number?.replace(/ /g, '').replace(/-/g, '').trim(),
			requestAt: this.requestAt
		});

		this.apiAccountService.verifyPhone(registerModel).then(
			() => {

				this.sharedService.setLoading(false);
				this.showModifyPhone = false;
			},
			(error: any) => {

				this.sharedService.setLoading(false); // hide loader
				// display error
				const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error al enviar el código de verificación.');
				this.sharedService.showErrorAlert(null, errMsg);
				this.showModifyPhone = true;
			}
		);

		this.onReset?.next(true);

		if (showAlert) {
			// Mostrar alerta
			this.codeAlert = 'Código de verificación reenviado';

			// Ocultar la alerta
			setTimeout(_ => this.codeAlert = null, 5000);
		}
	}

	sendCode() {

		this.showModifyPhone = false;
		this.onVerifyPhone();
	}

	changePhone() {

		this.showModifyPhone = true;
	}

	onSubmit() {

		const registerPhoneDto = new RegisterPhoneModel();
		registerPhoneDto.id = this.user.id;
		registerPhoneDto.phoneCountry = this.phone.value?.dialCode;
		registerPhoneDto.phone = this.phone.value?.number?.replace(/ /g, '').replace(/-/g, '').trim();
		registerPhoneDto.code = this.codeConfirmation.value?.trim();
		registerPhoneDto.requestAt = this.requestAt;

		this.sharedService.setLoading(true);

		const session = this.apiAccountService.getSession();

		this.apiAccountService.registerPhone(registerPhoneDto).then(
			(result: RegisterPhoneModel) => {

				this.validVerificationPhone.next(false);
				this.sharedService.setLoading(false);
				session.user.phoneVerifiedAt = result.phoneVerifiedAt ?? new Date();
				SessionManager.setSession(session);
			},
			(error: any) => {

				this.sharedService.setLoading(false); // hide loader
				// display error
				const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error al registrar la cuenta.');
				this.sharedService.showErrorAlert(null, errMsg);
			}
		);
	}

	onLogout() {

        this.sharedService.showConfirmAlert(
            null,
            `¿Deseás cerrar la sesión?`
        ).then(
            (result: SweetAlertResult) => {

                if (result && result.value) {

                    this.sharedService.logout();
					this.router.navigate(['/login']);
                }
            }
        );
    }
}
